import { MediaTable, MediaTableRef } from "../MediaTable/MediaTable";
import { FilterBarButtonWithModal, SearchBar, ExploreSideBarLayout } from "@kaltura/mediaspace-shared-search";
import {
    BaseEntryResultType,
    FilterValues,
    SearchAndSelectEntry,
    SearchAndSelectEntryList,
} from "@kaltura/mediaspace-shared-types";
import { useEffect, useMemo, useRef, useState } from "react";
import { extractDefaultFilterValues, translate, usePostKmsData } from "@kaltura/mediaspace-shared-utils";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { SearchAndSelectTabProps } from "./SearchAndSelectTabProps";
import { CreationToolsButtons } from "../creation-tools/CreationToolsButtons";

/**
 * Search and Select component - single tab. allows to search entries, displays them as a table with a set of actions
 */
export const SingleSourceTab = ({
    categoryId,
    mediaContext,
    filters,
    filterMenuProps,
    filterValues: initialValues,
    dataUrl,
    pageSize,
    columns,
    actions,
    showExploreButton,
    onExplore,
    selectionMode,
    onSelectionChange,
    selectedItemsResetter,
    creationToolsButtons,
    onCreationToolSelected,
    preCreationToolComponent,
    conditionalMessages,
    id,
    showBaseInfoOnlyInExploreBar,
    columnsWidth,
}: SearchAndSelectTabProps) => {
    const mediaTableRef = useRef<MediaTableRef | null>(null);

    const [exploreEntry, setExploreEntry] = useState<SearchAndSelectEntry>();
    const [exploreEntryIndex, setExploreEntryIndex] = useState<number>();
    const convertedExploreEntry = useMemo(
        () =>
            exploreEntry
            && ({
                object: exploreEntry.entry,
                itemsData: [],
            } as unknown as BaseEntryResultType),
        [exploreEntry]
    );

    useEffect(() => {
        if (exploreEntry === undefined && exploreEntryIndex !== undefined) {
            mediaTableRef.current?.focusExploreButton(exploreEntryIndex);
        }
    }, [exploreEntry, exploreEntryIndex]);

    const [page, setPage] = useState(1);

    // filter values as the user set them (+ defaults)
    const defaultFilterValues = useMemo(
        () => (filters ? { ...extractDefaultFilterValues(filters), ...initialValues } : {}),
        [filters, initialValues]
    );
    const [filterValues, setFilterValues] = useState<Record<string, any>>(defaultFilterValues);

    const searchData = useMemo(() => ({ ...filterValues, page }), [filterValues, page]);
    const [loadingState, response] = usePostKmsData<SearchAndSelectEntryList>(dataUrl, searchData);

    const handleFilterChange = (values: Partial<FilterValues>) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setPage(1);

        setExploreEntry(undefined);
        setExploreEntryIndex(undefined);
    };
    const handleKeywordSearchSubmit = (value: string) => handleFilterChange({ keyword: value });

    const isKeywordSearch = !!filterValues["keyword"];
    const sortBy = filterValues["sortBy"] ?? "relevance";
    const defaultSortBy = isKeywordSearch ? "relevance" : defaultFilterValues["sortBy"] ?? "relevance";
    const handleSortByChange = (sortBy: string) => handleFilterChange({ sortBy });

    return (
        <ExploreSideBarLayout
            searchResult={convertedExploreEntry}
            filterValues={filterValues}
            categoryId={categoryId}
            mediaContext={mediaContext}
            showPlayer={true}
            showBaseInfoOnly={showBaseInfoOnlyInExploreBar}
            callToActionComponent={exploreEntry && actions && actions(exploreEntry, true, mediaContext)}
            onClose={() => setExploreEntry(undefined)}
        >
            <Stack direction={"column"} gap={3}>
                <SearchBar
                    leftButtons={
                        filters
                            ? [
                                  <FilterBarButtonWithModal
                                      filters={filters}
                                      values={filterValues}
                                      onChange={handleFilterChange}
                                  />,
                              ]
                            : []
                    }
                    keywordSearchProps={{
                        onSubmit: handleKeywordSearchSubmit,
                        value: filterValues["keyword"] as string,
                        placeholder: translate("Search"),
                        ariaLabel: translate("Search"),
                    }}
                    filterMenuProps={
                        filterMenuProps && {
                            ...filterMenuProps,
                            values: filterValues,
                            onChange: handleFilterChange,
                        }
                    }
                    buttons={
                        creationToolsButtons && onCreationToolSelected
                            ? [
                                  <CreationToolsButtons
                                      creationToolButtons={creationToolsButtons}
                                      onClick={(creationTool, mediaContext) =>
                                          onCreationToolSelected(creationTool, preCreationToolComponent, mediaContext)
                                      }
                                      mediaContext={mediaContext}
                                  />,
                              ]
                            : undefined
                    }
                />

                <MediaTable
                    ref={mediaTableRef}
                    entries={response?.objects}
                    totalCount={response?.totalCount ?? 0}
                    isLoading={loadingState === "loading"}
                    isError={loadingState === "error"}
                    pageSize={pageSize}
                    page={page}
                    onPageChange={setPage}
                    columns={columns}
                    columnsWidth={columnsWidth}
                    actions={actions ? (entry) => actions(entry, false, mediaContext) : undefined}
                    showExploreButton={showExploreButton}
                    onExplore={(entry, index) => {
                        setExploreEntry(entry);
                        setExploreEntryIndex(index);
                        onExplore?.(entry.entry.id);
                    }}
                    sortBy={sortBy}
                    defaultSortBy={defaultSortBy}
                    onSortByChange={handleSortByChange}
                    isKeywordSearch={isKeywordSearch}
                    selectionMode={selectionMode}
                    onSelectionChange={
                        onSelectionChange ? (selection) => onSelectionChange?.(selection, id ?? 0) : undefined
                    }
                    selectedItemsResetIndicator={selectedItemsResetter}
                    conditionalMessages={conditionalMessages}
                />
            </Stack>
        </ExploreSideBarLayout>
    );
};
